/**
 * NeoCoreReferidosPage — módulo HUNTER (referidos en contexto emergente).
 *
 * Parte de quién progresa hacia su siguiente nivel: 4/9/15/22 pagos puntuales +
 * 1/2/3/4 referidos activos (Plus/Pro/Premium/Black). Ordenado por el más
 * próximo a subir; filtros por tier y "a un paso". Cada asociado puede invitar
 * a muchos. Sin dependencias externas (versión de transición previa a la
 * re-plataforma shadcn, ver ADR 014). Fuente: RPC reporte_referidos_emergente().
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowsClockwise, DownloadSimple, MagnifyingGlass, CaretDown, CalendarCheck, Users, WhatsappLogo } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { exportarExcel } from '@/lib/exportReport'
import { fetchConfigReporte } from '@/lib/useConfigReporte'

type Referido = { neo: string | null; nombre: string; invitado_at: string; activo: boolean; pago_cuota: boolean }
type Tier = { codigo: string; nombre: string; nivel: number; pagos_req: number; refs_req: number }
type Referidor = {
  referidor_id: string; neo: string | null; nombre: string; telefono: string | null; tier_cod: string; tier_nombre: string
  next_nombre: string | null; next_monto: number | null; es_max: boolean
  pagos: number; pagos_req: number; faltan_pagos: number; met_pagos: boolean
  refs_activos: number; refs_req: number; faltan_refs: number; met_refs: boolean
  invitados: number; progreso_pct: number; califica: boolean; referidos: Referido[]
}
type Referidor2 = Referidor & { a_un_paso: boolean }
type Reporte = {
  generado_at: string; tiers: Tier[]
  resumen: {
    en_juego: number; referidores: number; califican: number; a_un_paso: number
    solo_falta_ref: number; solo_falta_pagos: number
    invitados_total: number; invitados_activos: number
  }
  referidores: Referidor2[]
}

// Normaliza para búsqueda: minúsculas + sin tildes/diacríticos ("josé" ≈ "jose").
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
const cap = (s: string | null) => (s ? s.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '')
const ini = (s: string) => s.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
const fmtFecha = (iso: string) => new Date(iso).toLocaleDateString('es-GT', { day: '2-digit', month: 'short' })
const OK = 'var(--ok, #10b981)'
const BRAND = 'var(--brand-500, #FF5B04)'

const primerNombre = (s: string) => cap(s.split(' ')[0] ?? '')
function waLink(rf: Referidor): string | null {
  const tel = (rf.telefono ?? '').replace(/\D/g, '')
  if (tel.length < 8) return null
  const n = primerNombre(rf.nombre)
  const next = rf.next_nombre ?? 'el siguiente nivel'
  let msg: string
  if (rf.califica) msg = `Hola ${n}, ya calificás para ${next}. Entrá a la app para activarlo.`
  else if (rf.faltan_refs > 0 && rf.faltan_pagos === 0) msg = `Hola ${n}, estás a un paso de ${next}. Invitá a alguien con tu código: cuando pida su crédito y lo pague, subís de nivel.`
  else if (rf.faltan_pagos > 0 && rf.faltan_refs === 0) msg = `Hola ${n}, te falta${rf.faltan_pagos > 1 ? 'n' : ''} ${rf.faltan_pagos} pago${rf.faltan_pagos > 1 ? 's' : ''} puntual${rf.faltan_pagos > 1 ? 'es' : ''} para llegar a ${next}. Vas muy bien.`
  else msg = `Hola ${n}, vas ${rf.progreso_pct}% hacia ${next}. Seguí con tus pagos puntuales e invitá a alguien para subir.`
  return `https://wa.me/${tel}?text=${encodeURIComponent(msg)}`
}

export function NeoCoreReferidosPage() {
  const [data, setData] = useState<Reporte | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tier, setTier] = useState('TODOS')
  const [foco, setFoco] = useState<'todos' | 'a_un_paso' | 'falta_ref' | 'falta_pagos'>('todos')
  const [q, setQ] = useState('')
  const [abierto, setAbierto] = useState<Record<string, boolean>>({})

  const load = useCallback(() => {
    setLoading(true); setError(null)
    supabase.rpc('reporte_referidos_emergente').then(({ data, error }) => {
      if (error) setError(error.message); else setData(data as Reporte)
      setLoading(false)
    })
  }, [])
  useEffect(() => { load() }, [load])

  const filtrados = useMemo(() => {
    if (!data) return []
    const n = norm(q.trim())
    return data.referidores.filter((r) => {
      if (tier !== 'TODOS' && r.tier_cod.toUpperCase() !== tier) return false
      if (foco === 'a_un_paso' && !r.a_un_paso) return false
      if (foco === 'falta_ref' && !(!r.es_max && r.met_pagos && !r.met_refs)) return false
      if (foco === 'falta_pagos' && !(!r.es_max && r.met_refs && !r.met_pagos)) return false
      if (n && !norm(`${r.nombre} ${r.neo ?? ''}`).includes(n)) return false
      return true
    })
  }, [data, tier, foco, q])

  const exportar = async () => {
    if (!data) return
    const config = await fetchConfigReporte()
    await exportarExcel({
      config,
      titulo: 'Hunter · camino al siguiente nivel',
      subtitulo: `${data.resumen.en_juego} en juego · ${data.resumen.a_un_paso} a un paso · ${data.resumen.califican} califican`,
      headers: ['Asociado', 'Neo.id', 'Tier', 'Va hacia', 'Pagos', 'Activos', 'Progreso', 'Invitados', 'Califica'],
      rows: data.referidores.map((r) => [
        cap(r.nombre), r.neo ?? '—', cap(r.tier_nombre), r.next_nombre ?? 'Máximo',
        `${r.pagos}/${r.pagos_req}`, `${r.refs_activos}/${r.refs_req}`, `${r.progreso_pct}%`, r.invitados, r.califica ? 'Sí' : 'No',
      ]),
      filename: 'hunter-referidos', columnasNumericas: [7],
    })
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--n-500)' }}>Cargando…</div>
  if (error) return <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><div className="error-banner" style={{ margin: 0 }}>{error}</div><button className="btn" onClick={load}>Reintentar</button></div>
  if (!data) return null
  const r = data.resumen

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--n-900)' }}>Hunter</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: BRAND, background: 'var(--brand-50, #fff5ee)', padding: '3px 9px', borderRadius: 20 }}>Camino al siguiente nivel</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--n-500)', marginTop: 2 }}>Ordenado por quién está más cerca de subir.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <MagnifyingGlass size={15} style={{ position: 'absolute', left: 10, top: 9, color: 'var(--n-400)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar asociado…"
              style={{ padding: '8px 10px 8px 30px', borderRadius: 9, border: '1px solid var(--n-200)', fontSize: 13, width: 210, background: 'var(--surface,#fff)', color: 'var(--n-800)' }} />
          </div>
          <button className="btn btn-ghost" onClick={load}><ArrowsClockwise size={16} /> Actualizar</button>
          <button className="btn" onClick={exportar}><DownloadSimple size={16} /> Excel</button>
        </div>
      </div>

      {/* Vincular referido manual (los que mandan el referido por el chat) */}
      <VincularCard onDone={load} />

      {/* Filtros por tier + a un paso */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label="Todos" active={tier === 'TODOS'} onClick={() => setTier('TODOS')} />
        {data.tiers.map((t) => <Chip key={t.codigo} label={t.nombre} active={tier === t.codigo} onClick={() => setTier(t.codigo)} />)}
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--n-200)', margin: '2px 4px' }} />
        <Chip label="A un paso" active={foco === 'a_un_paso'} accent onClick={() => setFoco((v) => (v === 'a_un_paso' ? 'todos' : 'a_un_paso'))} />
        <Chip label="Solo falta referido" active={foco === 'falta_ref'} accent onClick={() => setFoco((v) => (v === 'falta_ref' ? 'todos' : 'falta_ref'))} />
        <Chip label="Solo faltan pagos" active={foco === 'falta_pagos'} accent onClick={() => setFoco((v) => (v === 'falta_pagos' ? 'todos' : 'falta_pagos'))} />
      </div>

      {/* KPIs · marco de promoción (los dos caminos) — tarjetas con aire */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
        <Kpi value={r.en_juego} label="en juego" />
        <Kpi value={r.califican} label="ya califican" color={OK} />
        <Kpi value={r.a_un_paso} label="a un paso" color={BRAND} />
        <Kpi value={r.solo_falta_ref} label="solo falta referido" />
        <Kpi value={r.solo_falta_pagos} label="solo faltan pagos" />
      </div>

      {/* Lista */}
      {filtrados.length === 0 ? (
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--n-400)', fontSize: 13 }}>Nadie cae en este filtro.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtrados.map((rf, i) => (
            <Row key={rf.referidor_id} rf={rf} rank={i + 1} open={!!abierto[rf.referidor_id]}
              onToggle={() => setAbierto((s) => ({ ...s, [rf.referidor_id]: !s[rf.referidor_id] }))} />
          ))}
        </div>
      )}

      <p style={{ color: 'var(--n-400)', fontSize: 11, margin: 0 }}>
        Actualizado {new Date(data.generado_at).toLocaleString('es-GT')} · para subir de nivel: cumplir los DOS caminos (pagos puntuales Y referido activo) · pago puntual = crédito emergente pagado sin mora · verde = camino cumplido / invitado que ya pagó. Solo referidos de asociación.
      </p>
    </div>
  )
}

function Chip({ label, active, accent, onClick }: { label: string; active: boolean; accent?: boolean; onClick: () => void }) {
  const bg = active ? (accent ? 'var(--brand-50,#fff5ee)' : 'var(--n-900)') : 'var(--surface,#fff)'
  const fg = active ? (accent ? 'var(--brand-700,#c43c00)' : '#fff') : 'var(--n-600)'
  const bd = active && accent ? 'var(--brand-200,#ffc7a3)' : 'var(--n-200)'
  return <button onClick={onClick} style={{ fontSize: 12, fontWeight: 500, background: bg, color: fg, border: `1px solid ${bd}`, borderRadius: 20, padding: '5px 13px', cursor: 'pointer' }}>{label}</button>
}

function Kpi({ value, label, color }: { value: number; label: string; color?: string }) {
  return (
    <div style={{ padding: '16px 12px', textAlign: 'center', background: 'var(--surface,#fff)', border: '1px solid var(--n-200)', borderRadius: 12 }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: color ?? 'var(--n-900)', lineHeight: 1 }}>{value.toLocaleString('es-GT')}</div>
      <div style={{ fontSize: 12, color: 'var(--n-500)', marginTop: 4 }}>{label}</div>
    </div>
  )
}

function invBg(x: Referido): string {
  if (x.activo) return 'var(--ok, #10b981)'
  if (x.pago_cuota) return '#EF9F27'
  return 'var(--n-300, #d4d4d8)'
}

function PathBar({ icon, label, have, need, met, blocker, note }: { icon: ReactNode; label: string; have: number; need: number; met: boolean; blocker: boolean; note?: string | null }) {
  const pct = need > 0 ? Math.min(100, Math.round((Math.min(have, need) / need) * 100)) : 100
  const color = met ? OK : blocker ? BRAND : 'var(--n-400)'
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--n-600)', marginBottom: 4 }}>
        <span style={{ color: 'var(--n-500)' }}>{icon} {label}</span>
        <strong style={{ color: met ? OK : 'var(--n-900)' }}>{have}/{need}{met ? ' ✓' : ''}</strong>
      </div>
      <div style={{ height: 6, background: 'var(--n-100)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(3, pct)}%`, height: '100%', background: color, transition: 'width .2s' }} />
      </div>
      {note ? <div style={{ fontSize: 10.5, color: 'var(--n-500)', marginTop: 3 }}>{note}</div> : null}
    </div>
  )
}

function Row({ rf, rank, open, onToggle }: { rf: Referidor2; rank: number; open: boolean; onToggle: () => void }) {
  const status = rf.califica ? { c: OK, bg: 'rgba(16,185,129,.1)', t: 'Califica' }
    : rf.es_max ? { c: 'var(--n-500)', bg: 'var(--n-100)', t: 'Nivel máximo' }
    : rf.met_pagos !== rf.met_refs ? { c: 'var(--brand-700,#c43c00)', bg: 'var(--brand-50,#fff5ee)', t: 'A un paso' }
    : { c: 'var(--n-500)', bg: 'var(--n-100)', t: 'En progreso' }
  const vis = (rf.referidos ?? []).slice(0, 5)
  const resto = (rf.referidos ?? []).length - vis.length
  const wa = waLink(rf)
  const nudge = rf.califica || rf.met_pagos !== rf.met_refs

  return (
    <div style={{ border: '1px solid var(--n-200)', borderRadius: 12, background: 'var(--surface,#fff)' }}>
      <div role="button" tabIndex={0} onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 16, width: '100%', padding: '14px 16px', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--n-400)', width: 16, textAlign: 'center', flexShrink: 0 }}>{rank}</span>
        <span style={{ width: 40, height: 40, borderRadius: 20, background: 'var(--brand-50,#fff5ee)', color: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{ini(rf.nombre)}</span>

        <div style={{ width: 188, flexShrink: 0, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--n-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cap(rf.nombre)}</div>
          <div style={{ fontSize: 12, color: 'var(--n-500)' }}>Neo.id {rf.neo ?? '—'} · {rf.tier_nombre}</div>
        </div>

        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--n-500)', marginBottom: 7 }}>
            <span>{rf.es_max ? 'Nivel máximo' : <>Va hacia <strong style={{ color: 'var(--n-900)' }}>{rf.next_nombre}</strong>{rf.next_monto ? <span style={{ color: 'var(--n-400)' }}> · Q{rf.next_monto}</span> : null}</>}</span>
            <strong style={{ color: 'var(--n-900)' }}>{rf.progreso_pct}%</strong>
          </div>
          {rf.es_max ? (
            <div style={{ height: 7, background: 'var(--n-100)', borderRadius: 4, overflow: 'hidden' }}><div style={{ width: '100%', height: '100%', background: OK }} /></div>
          ) : (
            <div style={{ display: 'flex', gap: 14 }}>
              <PathBar icon={<CalendarCheck size={12} style={{ verticalAlign: -2 }} />} label="Disciplina"
                have={rf.pagos} need={rf.pagos_req} met={rf.met_pagos} blocker={rf.a_un_paso && !rf.met_pagos} />
              <PathBar icon={<Users size={12} style={{ verticalAlign: -2 }} />} label="Red"
                have={rf.refs_activos} need={rf.refs_req} met={rf.met_refs} blocker={rf.a_un_paso && !rf.met_refs}
                note={rf.invitados > rf.refs_activos ? `trajo ${rf.invitados} · ${rf.refs_activos} activo${rf.refs_activos === 1 ? '' : 's'} (falta que paguen)` : null} />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexShrink: 0 }}>
          {vis.map((x, idx) => (
            <span key={(x.neo ?? '') + x.nombre} title={`${cap(x.nombre)} · ${x.activo ? 'pagó (activo)' : x.pago_cuota ? 'pagó con atraso' : 'aún no paga'}`}
              style={{ width: 28, height: 28, borderRadius: 14, background: invBg(x), color: '#fff', border: '2px solid var(--surface,#fff)', marginLeft: idx ? -8 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{ini(x.nombre)}</span>
          ))}
          {resto > 0 ? <span style={{ width: 28, height: 28, borderRadius: 14, background: 'var(--n-200)', color: 'var(--n-600)', border: '2px solid var(--surface,#fff)', marginLeft: -8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>+{resto}</span> : null}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 168, justifyContent: 'flex-end', flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: status.c, background: status.bg, borderRadius: 6, padding: '3px 9px' }}>{status.t}</span>
          {wa ? (
            <a href={wa} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Recordar por WhatsApp"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                color: nudge ? '#fff' : OK, background: nudge ? OK : 'rgba(16,185,129,.1)', border: nudge ? 'none' : '1px solid rgba(16,185,129,.25)' }}>
              <WhatsappLogo size={16} weight="fill" />
            </a>
          ) : null}
          <CaretDown size={14} color="var(--n-400)" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </div>
      </div>

      {open && (
        <div style={{ padding: '4px 16px 14px', borderTop: '1px solid var(--n-100)' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--n-400)', fontWeight: 700, margin: '10px 0 8px' }}>A quiénes trajo ({rf.invitados})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {(rf.referidos ?? []).map((x) => (
              <div key={(x.neo ?? '') + x.nombre} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 7, background: invBg(x), flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: 'var(--n-800)' }}>{cap(x.nombre)}</span>
                  <span style={{ fontSize: 12, color: 'var(--n-400)' }}>· Neo.id {x.neo ?? '—'}</span>
                </div>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--n-400)' }}>{fmtFecha(x.invitado_at)}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, width: 96, textAlign: 'right', color: x.activo ? OK : x.pago_cuota ? '#BA7517' : 'var(--n-400)' }}>{x.activo ? 'pagó · activo' : x.pago_cuota ? 'pagó c/ atraso' : 'aún no paga'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function VincularCard({ onDone }: { onDone: () => void }) {
  const [refr, setRefr] = useState('')
  const [rido, setRido] = useState('')
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const inputStyle = {
    padding: '8px 10px', borderRadius: 9, border: '1px solid var(--n-200)', fontSize: 13,
    width: 150, background: 'var(--surface,#fff)', color: 'var(--n-800)',
  } as const

  const vincular = async () => {
    if (!refr.trim() || !rido.trim()) { setMsg({ ok: false, text: 'Poné los dos Neo.id.' }); return }
    setBusy(true); setMsg(null)
    const { data, error } = await supabase.rpc('vincular_referido_manual', {
      p_referidor_neo: refr.trim(), p_referido_neo: rido.trim(),
    })
    setBusy(false)
    if (error) { setMsg({ ok: false, text: error.message }); return }
    const d = data as { referidor: string; referido: string; activado: boolean; referido_ya_pago: boolean }
    const extra = d.activado
      ? ' · activado (ya pagó) ✓'
      : d.referido_ya_pago ? ' · ya estaba activo' : ' · queda pendiente hasta que el referido pague'
    setMsg({ ok: true, text: `${cap(d.referidor)} ← ${cap(d.referido)}${extra}` })
    setRefr(''); setRido('')
    onDone()
  }

  return (
    <div className="card" style={{ padding: 14 }}>
      <button onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%' }}>
        <Users size={16} style={{ color: BRAND }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--n-900)' }}>Vincular referido manual</span>
        <span style={{ fontSize: 11, color: 'var(--n-500)' }}>· para los que lo mandan por el chat</span>
        <CaretDown size={14} style={{ marginLeft: 'auto', color: 'var(--n-400)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--n-500)' }}>
            Neo.id del referidor (quien invitó)
            <input value={refr} onChange={(e) => setRefr(e.target.value)} placeholder="857863" style={inputStyle} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--n-500)' }}>
            Neo.id del referido (el nuevo)
            <input value={rido} onChange={(e) => setRido(e.target.value)} placeholder="126500" style={inputStyle} />
          </label>
          <button className="btn" disabled={busy} onClick={vincular}>{busy ? 'Vinculando…' : 'Vincular'}</button>
        </div>
      )}
      {msg && (
        <div style={{ marginTop: 10, fontSize: 12, color: msg.ok ? OK : 'var(--danger,#e5484d)' }}>{msg.text}</div>
      )}
    </div>
  )
}

export default NeoCoreReferidosPage
